import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { App as AntApp, Input, Switch, Button } from 'antd';
import AppBar from '../components/AppBar';
import FileViewerLazy from '../components/FileViewerLazy';
import { AsyncButton, Skeletons, EmptyState, InfiniteSentinel, dt, initials } from '../components/kit';
import { usePaginatedList } from '../hooks/usePaginatedList';
import { AVATAR_IMG } from '../lib/avatar';
import { nationalityLabel } from '../lib/nationalities';
import { checkUpload } from '../lib/uploadLimits';
import * as profileApi from '../api/profile';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useUnsavedChanges } from '../context/UnsavedChangesContext';

// Self-editable agent fields (server whitelist SELF_EDITABLE), grouped so each MY DETAILS
// row opens ONLY its own section. email / REN / tier / rank stay HQ-managed (read-only).
const EDIT_GROUPS = [
  {
    key: 'contact',
    label: 'Contact details',
    fields: [
      { key: 'display_name', label: 'Display name' },
      { key: 'phone', label: 'Phone' },
    ],
  },
  {
    key: 'bank',
    label: 'Bank account',
    fields: [
      { key: 'bank_name', label: 'Bank name' },
      { key: 'bank_account_name', label: 'Bank account name' },
      { key: 'bank_account_number', label: 'Bank account number' },
    ],
  },
  {
    key: 'address',
    label: 'Address & emergency contact',
    fields: [
      { key: 'address', label: 'Address' },
      { key: 'covering_area', label: 'Covering area' },
      { key: 'emergency_contact_1_name', label: 'Emergency contact 1 — name' },
      { key: 'emergency_contact_1_mobile', label: 'Emergency contact 1 — mobile' },
      { key: 'emergency_contact_1_relationship', label: 'Emergency contact 1 — relationship' },
      { key: 'emergency_contact_2_name', label: 'Emergency contact 2 — name' },
      { key: 'emergency_contact_2_mobile', label: 'Emergency contact 2 — mobile' },
      { key: 'emergency_contact_2_relationship', label: 'Emergency contact 2 — relationship' },
    ],
  },
  {
    key: 'tax',
    label: 'Personal & tax info',
    fields: [
      { key: 'income_tax_no', label: 'Income tax no' },
      { key: 'ssm_no', label: 'SSM / company reg no' },
    ],
  },  // Pre-fills every event registration form; HQ sees it on the participant list.
  {
    key: 'events',
    label: 'Events & dietary',
    fields: [
      { key: 'dietary_restrictions', label: 'Dietary restrictions', placeholder: 'e.g. Vegetarian, Halal, No beef', max: 200 },
    ],
  },
];

export default function ProfilePage() {
  const navigate = useNavigate();
  const { message, modal } = AntApp.useApp();
  const { dark, toggle } = useTheme();
  const { logout } = useAuth();

  const [me, setMe] = useState(null);
  const [editing, setEditing] = useState(null); // an EDIT_GROUPS entry while editing, else null
  const [form, setForm] = useState({});

  const [prefs, setPrefs] = useState({ push_enabled: true, leaderboard_visible: true, website_visible: true });
  const [savingKey, setSavingKey] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [fileView, setFileView] = useState(null); // { name, url } → in-app PDF/image viewer
  const docFileRef = useRef(null);
  const [pw, setPw] = useState({ current_password: '', new_password: '', confirm: '' });
  const [savingPw, setSavingPw] = useState(false);

  const docs = usePaginatedList(profileApi.listMeDocuments, { limit: 10 });
  const docCount = docs.pagination?.total ?? docs.items.length;

  useEffect(() => {
    profileApi
      .listMe()
      .then((r) => {
        const m = r?.data ?? r;
        setMe(m);
        // leaderboard + website consent are persisted agents columns, not client preferences
        setPrefs((p) => ({
          ...p,
          leaderboard_visible: m?.leaderboard ?? p.leaderboard_visible,
          website_visible: m?.website ?? p.website_visible,
        }));
      })
      .catch((e) => message.error(e.message));
    // Hydrate persisted client preferences (agents.app_preferences) so toggles show truth.
    profileApi
      .listMePreferences()
      .then((r) => {
        const s = r?.data ?? r ?? {};
        setPrefs((p) => ({
          ...p,
          push_enabled: s.push_enabled ?? p.push_enabled,
        }));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Avatar upload — file → data URL → POST → agents.profile_picture_url.
  const avatarRef = useRef(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const handleAvatarFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { message.warning('Keep the photo under 3 MB'); return; }
    setUploadingAvatar(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result);
        fr.onerror = () => reject(new Error('Could not read the image'));
        fr.readAsDataURL(file);
      });
      const r = await profileApi.createMeProfilePicture({ image_data_url: dataUrl });
      const d = r?.data ?? r;
      setMe((m) => ({ ...m, profile_picture_url: d.profile_picture_url }));
      message.success('Profile photo updated');
    } catch (err) {
      message.error(err.message || 'Upload failed');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const beginEdit = (group) => {
    setForm(group.fields.reduce((a, f) => ({ ...a, [f.key]: me?.[f.key] ?? '' }), {}));
    setEditing(group);
  };

  const saveDetails = async () => {
    await profileApi.updateMe(form);
    setMe((m) => ({ ...m, ...form }));
    setEditing(null);
    message.success('Details updated');
  };

  // The open MY DETAILS group has changes while any field differs from the record
  // it was seeded from — so typing a value back to what it was is not a change.
  const detailsDirty = !!editing
    && editing.fields.some((f) => String(form[f.key] ?? '') !== String(me?.[f.key] ?? ''));
  const detailsGuard = useUnsavedChanges(detailsDirty, {
    what: editing ? `your ${editing.label.toLowerCase()}` : 'your details',
    saveLabel: 'Save & leave',
    onSave: async () => {
      try {
        await saveDetails();
        return true;
      } catch (e) {
        message.error(e.message);
        return false;
      }
    },
  });
  // A half-typed new password is worth a warning. The CURRENT password alone is
  // not counted: a password manager can fill that box on its own, and a page that
  // then refused to let the agent leave would be guarding nothing they typed.
  useUnsavedChanges(!!(pw.new_password || pw.confirm), { what: 'your new password' });

  const togglePref = async (key) => {
    const next = !prefs[key];
    setSavingKey(key);
    setPrefs((p) => ({ ...p, [key]: next }));
    try {
      await profileApi.updateMePreferences({ [key]: next });
    } catch (e) {
      setPrefs((p) => ({ ...p, [key]: !next }));
      message.error(e.message);
    } finally {
      setSavingKey(null);
    }
  };

  // Leaderboard consent is a persisted agents column — it goes through /me/visibility,
  // NOT the (ephemeral) preferences echo endpoint.
  const toggleLeaderboard = async () => {
    const next = !prefs.leaderboard_visible;
    setSavingKey('leaderboard_visible');
    setPrefs((p) => ({ ...p, leaderboard_visible: next }));
    try {
      await profileApi.updateMeVisibility({ leaderboard: next });
      setMe((m) => ({ ...m, leaderboard: next }));
    } catch (e) {
      setPrefs((p) => ({ ...p, leaderboard_visible: !next }));
      message.error(e.message);
    } finally {
      setSavingKey(null);
    }
  };

  // Public-website listing consent is a persisted agents column — like leaderboard it
  // goes through /me/visibility, not the (ephemeral) preferences echo endpoint.
  const toggleWebsite = async () => {
    const next = !prefs.website_visible;
    setSavingKey('website_visible');
    setPrefs((p) => ({ ...p, website_visible: next }));
    try {
      await profileApi.updateMeVisibility({ website: next });
      setMe((m) => ({ ...m, website: next }));
    } catch (e) {
      setPrefs((p) => ({ ...p, website_visible: !next }));
      message.error(e.message);
    } finally {
      setSavingKey(null);
    }
  };

  // Signed-URL document upload (private 'agent-documents' bucket).
  const handleDocFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const problem = checkUpload(file, 'agent-documents');
    if (problem) { message.warning(problem); return; }
    setUploadingDoc(true);
    try {
      const ct = file.type || 'application/octet-stream';
      const up = (await profileApi.createMeDocumentsUploadUrl({ fileName: file.name, fileSize: file.size, contentType: ct }))?.data;
      if (!up?.uploadUrl) throw new Error('Could not get an upload URL');
      try {
        await axios.put(up.uploadUrl, file, { headers: { 'Content-Type': ct, 'x-upsert': 'false' } });
      } catch {
        throw new Error('Could not upload this file. Please try again.');
      }
      await profileApi.createMeDocumentsConfirm({ storagePath: up.path, fileName: file.name, fileSize: file.size, contentType: ct });
      message.success('Document uploaded');
      docs.refresh();
    } catch (err) {
      message.error(err.message || 'Upload failed');
    } finally {
      setUploadingDoc(false);
    }
  };

  // Confirmed: these are the agent's own identity and licence documents, deleted from
  // storage outright, and HQ may be relying on one being on file.
  const removeDoc = (attId, fileName) => {
    modal.confirm({
      title: 'Delete this document?',
      content: (
        <span>
          <b>{fileName || 'This document'}</b> is deleted from your profile and from storage.
          This cannot be undone — you would have to upload it again.
        </span>
      ),
      okText: 'Delete',
      okButtonProps: { danger: true },
      cancelText: 'Keep it',
      centered: true,
      onOk: async () => {
        try {
          await profileApi.deleteMeDocumentsByAttachmentId(attId);
        } catch (e) {
          message.error(e.message);
          throw e; // keep the dialog open on a server refusal
        }
        message.success('Document removed');
        docs.refresh();
      },
    });
  };

  const changePassword = async () => {
    if (!pw.current_password || !pw.new_password) { message.warning('Enter your current and new password'); return; }
    if (pw.new_password.length < 8) { message.warning('New password must be at least 8 characters'); return; }
    if (pw.new_password !== pw.confirm) { message.warning('New passwords do not match'); return; }
    setSavingPw(true);
    try {
      await profileApi.updateMePassword({ current_password: pw.current_password, new_password: pw.new_password });
      message.success('Password changed');
      setPw({ current_password: '', new_password: '', confirm: '' });
    } catch (e) {
      message.error(e.message);
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <AppBar back title="Profile & settings" />

      <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {!me ? (
          <Skeletons n={4} height={72} />
        ) : (
          <>
            {/* profile card — tap the avatar to change the photo */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, background: 'linear-gradient(135deg,#241854,#18345E)', borderRadius: 16, padding: 18, color: '#fff' }}>
              <input ref={avatarRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleAvatarFile} style={{ display: 'none' }} />
              <div
                onClick={() => avatarRef.current?.click()}
                style={{ width: 54, height: 54, borderRadius: '50%', background: 'rgba(255,255,255,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, fontWeight: 700, flex: 'none', cursor: 'pointer', overflow: 'hidden', position: 'relative', opacity: uploadingAvatar ? 0.6 : 1 }}
              >
                {me.profile_picture_url
                  ? <img src={me.profile_picture_url} alt="" style={AVATAR_IMG} />
                  : initials(me.full_name)}
                <span style={{ position: 'absolute', right: 0, bottom: 0, left: 0, background: 'rgba(0,0,0,.42)', fontSize: 10.5, fontWeight: 600, textAlign: 'center', padding: '2px 0', letterSpacing: '.04em' }}>EDIT</span>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 600 }}>{me.full_name}</div>
                <div style={{ fontSize: 11.5, opacity: 0.8 }}>
                  {'REN ' + String(me.ren_no || '').replace(/^REN\s*/i, '')} · {me.gci_tier}% · {me.management_rank}
                </div>
              </div>
            </div>

            {/* REN-expiry reminder */}
            {(() => {
              const d = me.ren_expired_date ? new Date(me.ren_expired_date) : null;
              if (!d || Number.isNaN(d.getTime())) return null;
              const days = Math.ceil((d.getTime() - Date.now()) / 86400000);
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'color-mix(in srgb,var(--warn) 10%,var(--surface))', border: '1px solid color-mix(in srgb,var(--warn) 30%,var(--border))', borderRadius: 12, padding: '14px 14px' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--warn)', flex: 'none' }} />
                  <div style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>
                    {days >= 0 ? (
                      <>REN expires <b>{dt(me.ren_expired_date)}</b> · {days} {days === 1 ? 'day' : 'days'}</>
                    ) : (
                      <>REN expired <b>{dt(me.ren_expired_date)}</b></>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* my details */}
            <div style={sectionLabel}>MY DETAILS</div>
            <div style={{ ...card, marginTop: -4 }}>
              {editing ? (
                <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{editing.label}</span>
                    <span onClick={() => detailsGuard.confirmClose(() => setEditing(null))} style={{ fontSize: 12.5, color: 'var(--muted)', cursor: 'pointer' }}>Cancel</span>
                  </div>
                  {editing.fields.map((f) => (
                    <div key={f.key}>
                      <div style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 5 }}>{f.label}</div>
                      <Input value={form[f.key]} onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))} placeholder={f.placeholder || f.label} maxLength={f.max} />
                    </div>
                  ))}
                  <AsyncButton type="primary" block onClick={saveDetails} onError={(e) => message.error(e.message)}>Save {editing.label.toLowerCase()}</AsyncButton>
                </div>
              ) : (
                EDIT_GROUPS.map((g, i) => (
                  <NavRow key={g.key} label={g.label} onClick={() => beginEdit(g)} last={i === EDIT_GROUPS.length - 1} />
                ))
              )}
            </div>

            {/* identity — HQ-managed record, read-only here (admin portal is the source of truth) */}
            <div style={sectionLabel}>IDENTITY · HQ RECORD</div>
            <div style={{ ...card, marginTop: -4, padding: '4px 14px' }}>
              {[
                ['IC number', me.ic_number],
                ['Passport number', me.passport_number],
                ['Nationality', nationalityLabel(me.nationality)],
                ['Race', me.race],
                ['Sex', me.sex],
                ['Join date', me.join_date ? dt(me.join_date) : null],
                ...(me.resign_date ? [['Resign date', dt(me.resign_date)]] : []),
              ].map(([label, value], i, arr) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 14, padding: '9px 0', borderBottom: i === arr.length - 1 ? 'none' : '1px solid var(--border)' }}>
                  <span style={{ fontSize: 13, color: 'var(--muted)' }}>{label}</span>
                  <span style={{ fontSize: 13, fontWeight: 500, textAlign: 'right' }}>{value || '—'}</span>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: -6, paddingLeft: 2 }}>
              Something wrong here? Contact HQ to update your identity record.
            </div>

            {/* my documents */}
            <div style={sectionLabel}>MY DOCUMENTS · {docCount}</div>
            <div style={{ ...card, marginTop: -4 }}>
              {docs.loading ? (
                <div style={{ padding: 16 }}><Skeletons n={2} height={40} /></div>
              ) : docs.empty ? (
                <EmptyState title="No documents" sub="IC / passport, REN certificate and other files appear here." />
              ) : (
                docs.items.map((d, i) => (
                  <div key={d.id} style={{ ...rowBase, borderBottom: i === docs.items.length - 1 ? 'none' : '1px solid var(--border)' }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, wordBreak: 'break-word' }}>{d.name}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2 }}>{d.type || 'Document'} · {dt(d.uploaded_at)}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flex: 'none' }}>
                      {d.signed_url && <Button size="small" onClick={() => setFileView({ name: d.name, url: d.signed_url })}>View</Button>}
                      <Button size="small" danger onClick={() => removeDoc(d.id, d.name)}>Delete</Button>
                    </div>
                  </div>
                ))
              )}
              <InfiniteSentinel hasMore={docs.hasMore} loading={docs.loadingMore} onReach={docs.loadMore} />
              <div style={{ padding: 12, borderTop: '1px solid var(--border)' }}>
                <input ref={docFileRef} type="file" onChange={handleDocFile} style={{ display: 'none' }} />
                <Button type="dashed" block loading={uploadingDoc} onClick={() => docFileRef.current?.click()}>
                  {uploadingDoc ? 'Uploading…' : '＋ Add document'}
                </Button>
              </div>
            </div>

            {/* preferences */}
            <div style={sectionLabel}>PREFERENCES</div>
            <div style={{ ...card, marginTop: -4 }}>
              <ToggleRow label="Dark theme" checked={dark} onChange={toggle} />
              <ToggleRow label="Show me on the leaderboard" checked={prefs.leaderboard_visible} loading={savingKey === 'leaderboard_visible'} onChange={toggleLeaderboard} />
              <ToggleRow label="Show my profile on the website" checked={prefs.website_visible} loading={savingKey === 'website_visible'} onChange={toggleWebsite} />
              <ToggleRow label="Push notifications" checked={prefs.push_enabled} loading={savingKey === 'push_enabled'} onChange={() => togglePref('push_enabled')} last />
            </div>

            {/* change password */}
            <div style={sectionLabel}>CHANGE PASSWORD</div>
            <div style={{ ...card, marginTop: -4, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Input.Password value={pw.current_password} onChange={(e) => setPw((s) => ({ ...s, current_password: e.target.value }))} placeholder="Current password" />
              <Input.Password value={pw.new_password} onChange={(e) => setPw((s) => ({ ...s, new_password: e.target.value }))} placeholder="New password (min 8 chars)" />
              <Input.Password value={pw.confirm} onChange={(e) => setPw((s) => ({ ...s, confirm: e.target.value }))} placeholder="Confirm new password" />
              <Button type="primary" block loading={savingPw} onClick={changePassword}>Update password</Button>
            </div>

            {/* HQ-only note */}
            <div style={{ background: 'var(--surface)', border: '1.5px dashed var(--border)', borderRadius: 14, padding: '15px 16px', fontSize: 12.5, color: 'var(--muted)' }}>
              Tier / rank overrides, freeze, revoke and email changes are managed by HQ admin.
            </div>

            {/* logout */}
            <AsyncButton
              danger
              block
              onClick={async () => {
                await logout();
                navigate('/login');
              }}
              onError={(e) => message.error(e.message)}
            >
              Log out
            </AsyncButton>
            <div style={{ height: 6 }} />
          </>
        )}
      </div>

      {/* In-app document viewer (popup-safe on mobile) */}
      {fileView && <FileViewerLazy title={fileView.name} url={fileView.url} onClose={() => setFileView(null)} />}
    </div>
  );
}

const sectionLabel = { fontSize: 11.5, fontWeight: 600, letterSpacing: '.08em', color: 'var(--muted)' };
const card = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' };
const rowBase = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14, padding: '15px 16px' };

const NavRow = ({ label, onClick, last }) => (
  <div onClick={onClick} style={{ ...rowBase, borderBottom: last ? 'none' : '1px solid var(--border)', cursor: 'pointer' }}>
    <span style={{ fontSize: 13, fontWeight: 500 }}>{label}</span>
    <span style={{ fontSize: 16, lineHeight: 1, color: 'var(--muted)' }}>›</span>
  </div>
);

const ToggleRow = ({ label, checked, loading, onChange, last }) => (
  <div style={{ ...rowBase, borderBottom: last ? 'none' : '1px solid var(--border)' }}>
    <span style={{ fontSize: 13, fontWeight: 500 }}>{label}</span>
    <Switch checked={checked} loading={loading} onChange={onChange} />
  </div>
);
